import { assetOptionFixtures } from "./assetOptionFixtures";

/**
 * Mock-only internal row shape -- carries `updatedAt` for the list's own
 * sort/filter (the real detail endpoint has no such field; see
 * SupervisorAssignmentDetail) and single-source Arabic names doubled into
 * both `nameEn`/`nameAr` slots (this data never reaches a real bilingual
 * audience, only the mocked list/detail paths).
 */
export interface MockSupervisorAssignmentRow {
  readonly supervisor: {
    readonly id: string;
    readonly nameEn: string;
    readonly nameAr: string;
    readonly username: string;
  };
  readonly assets: readonly { readonly id: string; readonly nameEn: string; readonly nameAr: string }[];
  readonly updatedAt: string;
}

// A rotating window of the shared asset catalog -- lets the same physical
// assets appear under more than one supervisor (an asset may be assigned to
// several supervisors) while keeping each supervisor's own list distinct.
function assetsFor(count: number, offset: number) {
  return Array.from({ length: count }, (_, index) => {
    const asset = assetOptionFixtures[(offset + index) % assetOptionFixtures.length]!;
    return { id: asset.id, nameEn: asset.nameEn, nameAr: asset.nameAr };
  });
}

// 23 rows -- enough for 3 pages at the default 10-per-page, matching the
// paginated list in the design mock.
export const supervisorAssignmentFixtures: readonly MockSupervisorAssignmentRow[] =
  [
    { name: "محمد بن عبدالله الغامدي", assignedAssetsCount: 12, updatedAt: "2027-01-17" },
    { name: "الحسين بن علي", assignedAssetsCount: 5, updatedAt: "2027-01-18" },
    { name: "علي بن أبي طالب", assignedAssetsCount: 10, updatedAt: "2027-01-15" },
    { name: "محمد بن عبدالله الغامدي", assignedAssetsCount: 10, updatedAt: "2027-01-16" },
    { name: "حسين بن علي", assignedAssetsCount: 10, updatedAt: "2027-01-18" },
    { name: "يوسف احمد محمد", assignedAssetsCount: 10, updatedAt: "2027-01-15" },
    { name: "محمد احمد علي", assignedAssetsCount: 10, updatedAt: "2027-01-16" },
    { name: "خالد بن الوليد", assignedAssetsCount: 8, updatedAt: "2027-01-14" },
    { name: "عمر بن الخطاب", assignedAssetsCount: 15, updatedAt: "2027-01-13" },
    { name: "سعد بن أبي وقاص", assignedAssetsCount: 6, updatedAt: "2027-01-12" },
    { name: "أحمد بن حنبل", assignedAssetsCount: 9, updatedAt: "2027-01-11" },
    { name: "عبدالرحمن بن عوف", assignedAssetsCount: 7, updatedAt: "2027-01-10" },
    { name: "طارق بن زياد", assignedAssetsCount: 11, updatedAt: "2027-01-09" },
    { name: "صلاح الدين الأيوبي", assignedAssetsCount: 14, updatedAt: "2027-01-08" },
    { name: "عمرو بن العاص", assignedAssetsCount: 4, updatedAt: "2027-01-07" },
    { name: "زيد بن ثابت", assignedAssetsCount: 13, updatedAt: "2027-01-06" },
    { name: "بلال بن رباح", assignedAssetsCount: 3, updatedAt: "2027-01-05" },
    { name: "سلمان الفارسي", assignedAssetsCount: 16, updatedAt: "2027-01-04" },
    { name: "أبو عبيدة بن الجراح", assignedAssetsCount: 2, updatedAt: "2027-01-03" },
    { name: "المقداد بن عمرو", assignedAssetsCount: 9, updatedAt: "2027-01-02" },
    { name: "عثمان بن عفان", assignedAssetsCount: 12, updatedAt: "2027-01-01" },
    { name: "حمزة بن عبدالمطلب", assignedAssetsCount: 7, updatedAt: "2026-12-31" },
    { name: "جعفر بن أبي طالب", assignedAssetsCount: 5, updatedAt: "2026-12-30" },
  ].map((row, index) => ({
    supervisor: {
      id: `supervisor-${String(index + 1)}`,
      nameEn: row.name,
      nameAr: row.name,
      username: `supervisor${String(index + 1)}`,
    },
    assets: assetsFor(row.assignedAssetsCount, index * 3),
    updatedAt: new Date(row.updatedAt).toISOString(),
  }));
